import { useState, useEffect, useRef } from "react";

const videos = [
  {
    url: "https://res.cloudinary.com/dz0b5eqof/video/upload/v1738670795/6245612-uhd_2160_4096_30fps_sfrqfy.mp4",
    type: "video/mp4"
  },
  {
    url: "https://res.cloudinary.com/dz0b5eqof/video/upload/v1738605406/5658288-uhd_3840_2160_25fps_ne8grx.mp4",
    type: "video/mp4"
  },
  {
    url: "https://res.cloudinary.com/dxa3i3h49/video/upload/v1736428807/4296918-uhd_3840_2160_25fps_okexuk.mp4",
    type: "video/mp4"
  },
  {
    url: "https://res.cloudinary.com/dxa3i3h49/video/upload/v1736428799/3044865-uhd_3840_2160_24fps_ebppj1.mp4",
    type: "video/mp4"
  }
];

const INTERVAL = 10000; // 10 seconds in milliseconds
const TRANSITION_DURATION = 2000; // 2 seconds for smooth crossfade

const VideoShowcase = () => {
  const [currentVideo, setCurrentVideo] = useState(0);
  const [nextVideo, setNextVideo] = useState(1);
  const [isInitialized, setIsInitialized] = useState(false);
  const videoRefs = useRef<HTMLVideoElement[]>([]);
  const intervalRef = useRef<NodeJS.Timeout>();
  const startTimeRef = useRef<number>(Date.now());

  // Initialize videos
  useEffect(() => {
    const initializeVideos = async () => {
      try {
        // Initialize video refs array
        videoRefs.current = videoRefs.current.slice(0, videos.length);

        // Load all videos first
        const loadPromises = videoRefs.current.map((video) => {
          if (!video) return Promise.resolve();
          
          return new Promise((resolve) => {
            const handleCanPlay = () => {
              video.removeEventListener('canplay', handleCanPlay);
              resolve(undefined);
            };
            video.addEventListener('canplay', handleCanPlay);
            
            // If video is already loaded
            if (video.readyState >= 3) {
              resolve(undefined);
            }
          });
        });

        await Promise.all(loadPromises);
        setIsInitialized(true);
      } catch (error) {
        console.error("Error initializing videos:", error);
      }
    };

    initializeVideos();

    return () => {
      videoRefs.current.forEach((video) => {
        if (video) {
          video.pause();
          video.removeAttribute('src');
          video.load();
        }
      });
    };
  }, []);

  // Handle video playback and transitions
  useEffect(() => {
    if (!isInitialized) return;

    const handleVideoEnd = (index: number) => {
      const video = videoRefs.current[index];
      if (!video) return;
      video.currentTime = 0;
    };

    // Set up video event listeners
    videoRefs.current.forEach((video, index) => {
      if (!video) return;
      video.addEventListener('ended', () => handleVideoEnd(index));
    });

    // Start playing current and next videos
    const playVideos = async () => {
      try {
        const currentVid = videoRefs.current[currentVideo];
        const nextVid = videoRefs.current[nextVideo];

        if (currentVid) {
          await currentVid.play();
        }
        if (nextVid) {
          await nextVid.play();
        }
      } catch (error) {
        console.error("Error playing videos:", error);
      }
    };

    playVideos();

    // Precise interval timing function
    const runPreciseInterval = () => {
      const now = Date.now();
      const nextTick = startTimeRef.current + INTERVAL;
      const delay = Math.max(0, nextTick - now);

      intervalRef.current = setTimeout(() => {
        try {
          setCurrentVideo(nextVideo);
          setNextVideo((nextVideo + 1) % videos.length);
          startTimeRef.current = nextTick;
          runPreciseInterval();
        } catch (error) {
          console.error("Error in interval execution:", error);
          startTimeRef.current = Date.now();
          runPreciseInterval();
        }
      }, delay);
    };

    runPreciseInterval();

    return () => {
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
      }
      videoRefs.current.forEach((video) => {
        if (video) {
          video.removeEventListener('ended', () => handleVideoEnd(videoRefs.current.indexOf(video)));
        }
      });
    };
  }, [isInitialized, currentVideo, nextVideo]);

  return (
    <div className="relative hidden lg:block">
      <div className="relative z-10 rounded-3xl overflow-hidden shadow-2xl h-[600px] animate-scale-in">
        {videos.map((video, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-${TRANSITION_DURATION} ${
              index === currentVideo 
                ? "opacity-100" 
                : index === nextVideo 
                  ? "opacity-0" 
                  : "opacity-0 pointer-events-none"
            }`}
          >
            <video
              ref={el => {
                if (el) videoRefs.current[index] = el;
              }}
              className="w-full h-full object-cover"
              autoPlay={false}
              muted
              loop
              playsInline
              preload="auto"
              crossOrigin="anonymous"
            >
              <source src={video.url} type={video.type} />
              Your browser does not support the video tag.
            </video>
          </div>
        ))}
      </div>
      <div className="absolute -bottom-4 -right-4 w-72 h-72 bg-accent/20 rounded-full blur-3xl"></div>
    </div>
  );
};

export default VideoShowcase;